import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { ShoppingCart, Warning } from '@mui/icons-material';
import { monthlyRefillsAPI, orderAPI, lookupAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Staff, RefillOrderReviewItem, RefillOrderReviewMetadata } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  storeId: number;
  storeName: string;
  /** Customers to order for. One id = per-customer order; many = one consolidated order. */
  customerIds: number[];
  /** Only set in single-customer mode — used for the order title. */
  customerName?: string;
  knobs: Record<string, number>;
  onCreated: (message: string) => void;
}

const inr = (n: string | number | null) =>
  n == null || n === ''
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/** Local editable copy of a review line. `qty` is in STRIPS — that is what an order stores. */
interface Line extends RefillOrderReviewItem {
  key: string;
  include: boolean;
  /** Strips to order. order_request_items.quantity is packs, not loose units. */
  qty: number;
  /** Demand minus what is already on the shelf, in strips. */
  toOrder: number;
}

const MonthlyRefillOrderReview: React.FC<Props> = ({
  open, onClose, storeId, storeName, customerIds, customerName, knobs, onCreated,
}) => {
  const { dbUser } = useAuth();
  const isBulk = customerIds.length > 1;

  const [lines, setLines] = useState<Line[]>([]);
  const [meta, setMeta] = useState<RefillOrderReviewMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showInStock, setShowInStock] = useState(false);

  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [staffId, setStaffId] = useState<number | ''>('');
  const [date, setDate] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    if (open) lookupAPI.getStaff().then(setStaffList).catch(() => {});
  }, [open]);

  const loadReview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await monthlyRefillsAPI.getOrderReview({
        store_id: storeId, customer_ids: customerIds, ...knobs,
      });
      // Quantity defaults to the SHORTFALL, not the full demand: on a typical
      // selection ~85% of lines are already covered by stock, so ordering the full
      // demand buys roughly 19x more than needed and creates the dead stock this
      // screen exists to avoid. Only lines with a real shortfall are ticked, and
      // sole-customer lines stay unticked until the customer confirms.
      setLines((res.data as RefillOrderReviewItem[]).map(r => ({
        ...r,
        key: `${r.medicine_name}|${r.pack_size ?? ''}`,
        toOrder: r.to_order_strips,
        qty: r.to_order_strips,
        include: r.to_order_strips > 0 && r.risk_category === 'covered',
      })));
      setMeta(res.metadata);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to build the order review');
      setLines([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, JSON.stringify(customerIds)]);

  useEffect(() => {
    if (open) { setStaffId(''); setDate(dayjs()); loadReview(); }
  }, [open, loadReview]);

  const setLine = (key: string, patch: Partial<Line>) =>
    setLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)));

  // Prioritise what to pick first when composing the OMS commitment order:
  // out-of-stock (none) at the top, then lowest stock, then higher stock last;
  // bigger shortage breaks ties. So staff tick the most urgent items first.
  const byStockPriority = (a: Line, b: Line) =>
    Number(a.current_stock) - Number(b.current_stock) ||
    b.shortfall_loose - a.shortfall_loose;

  // Only lines with a shortfall are worth buying; the rest are already on the shelf.
  const needed = lines.filter(l => l.toOrder > 0);
  const covered = needed.filter(l => l.risk_category === 'covered').sort(byStockPriority);
  const sole = needed.filter(l => l.risk_category === 'sole').sort(byStockPriority);
  const inStock = lines.filter(l => l.toOrder === 0).sort(byStockPriority);
  const chosen = lines.filter(l => l.include && l.qty > 0);
  const totalStrips = chosen.reduce((s, l) => s + l.qty, 0);
  const totalLoose = chosen.reduce((s, l) => s + l.qty * l.units_per_pack, 0);

  const handleCreate = async () => {
    if (!staffId) { setError('Please select a staff member.'); return; }
    if (chosen.length === 0) { setError('Tick at least one medicine to order.'); return; }
    if (!date) { setError('Please pick a date.'); return; }

    setSaving(true);
    setError('');
    try {
      const orderDate = date.format('YYYY-MM-DD');
      const payload: any = {
        date: orderDate,
        staff_id: staffId as number,
        store_id: storeId,
        status: 'pending',
        created_by: dbUser?.email || 'system',
        title: isBulk
          ? `Monthly Refills - ${storeName} - ${orderDate} (${customerIds.length} customers)`
          : `Refill - ${customerName || 'Customer'} - ${orderDate}`,
        items: chosen.map(l => ({
          title: l.medicine_name,
          manufacturer_id: l.manufacturer_id,
          quantity: l.qty,
        })),
      };
      // A consolidated order spans many customers, so it deliberately carries no
      // customer_id — the same shape as the 379 existing orders that have none.
      if (!isBulk) payload.customer_id = customerIds[0];

      const order = await orderAPI.create(payload);
      onCreated(`Order #${(order as any)?.id ?? ''} created with ${chosen.length} item${chosen.length === 1 ? '' : 's'} (${totalStrips} strips).`);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to create the order');
    } finally {
      setSaving(false);
    }
  };

  const renderTable = (rows: Line[], variant: 'covered' | 'sole' | 'instock') => (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { bgcolor: variant === 'sole' ? '#fff4e5' : '#f5f5f5', fontWeight: 600 } }}>
            <TableCell padding="checkbox">
              <Checkbox size="small"
                checked={rows.length > 0 && rows.every(r => r.include)}
                indeterminate={rows.some(r => r.include) && !rows.every(r => r.include)}
                onChange={e => {
                  const keys = new Set(rows.map(r => r.key));
                  setLines(prev => prev.map(l => (keys.has(l.key) ? { ...l, include: e.target.checked } : l)));
                }}
              />
            </TableCell>
            <TableCell>Medicine</TableCell>
            <TableCell>Pack</TableCell>
            <TableCell align="right">Need</TableCell>
            <TableCell align="right">In Stock</TableCell>
            <TableCell align="right">Short</TableCell>
            <TableCell align="right">To Order<br /><small>(strips)</small></TableCell>
            <TableCell align="center">Buyers</TableCell>
            {isBulk && <TableCell>For</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(l => (
            <TableRow key={l.key} hover selected={l.include}>
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={l.include}
                  onChange={e => setLine(l.key, { include: e.target.checked })} />
              </TableCell>
              <TableCell>{l.medicine_name}</TableCell>
              <TableCell>{l.pack_size || '—'}</TableCell>
              <TableCell align="right">{Number(l.total_qty).toLocaleString('en-IN')}</TableCell>
              <TableCell align="right"
                sx={{ color: Number(l.current_stock) <= 0 ? '#c62828'
                        : l.toOrder > 0 ? '#ed6c02' : '#2e7d32',
                      fontWeight: 600 }}>
                {Number(l.current_stock) <= 0 ? 'none' : Number(l.current_stock).toLocaleString('en-IN')}
              </TableCell>
              <TableCell align="right">{l.shortfall_loose.toLocaleString('en-IN')}</TableCell>
              <TableCell align="right">
                <Tooltip title={`${l.units_per_pack} loose per strip — ${l.shortfall_loose} short rounds up to ${l.toOrder} strip${l.toOrder === 1 ? '' : 's'} (${l.qty * l.units_per_pack} loose)`}>
                  <TextField type="number" size="small" value={l.qty}
                    onChange={e => setLine(l.key, { qty: parseInt(e.target.value) || 0 })}
                    inputProps={{ min: 0 }} sx={{ width: 80 }} />
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Chip size="small" label={l.buyer_count}
                  color={l.risk_category === 'sole' ? 'warning' : 'default'}
                  variant={l.risk_category === 'sole' ? 'filled' : 'outlined'} />
              </TableCell>
              {isBulk && (
                <TableCell>
                  <Tooltip title={l.customers.map(c => `${c.customer_name} (${c.qty})`).join(', ')}>
                    <span>
                      {l.customers.length === 1
                        ? l.customers[0].customer_name
                        : `${l.customers.length} customers`}
                    </span>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShoppingCart color="primary" />
        {isBulk
          ? `Create order — ${customerIds.length} customers, ${storeName}`
          : `Create order — ${customerName}, ${storeName}`}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2, mt: 1 }}>
          <DatePicker label="Order date" value={date} onChange={setDate}
            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }} />
          <FormControl size="small" sx={{ minWidth: 200 }} required>
            <InputLabel>Staff</InputLabel>
            <Select value={staffId} label="Staff" onChange={e => setStaffId(e.target.value as number)}>
              {staffList.map(s => <MenuItem key={s.id} value={s.id}>{s.staff_name}</MenuItem>)}
            </Select>
          </FormControl>
          {meta && (
            <Typography variant="body2" color="textSecondary">
              {meta.item_count} due · {needed.length} need ordering · {inStock.length} already in stock
            </Typography>
          )}
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Medicines already collected this cycle are left out. <strong>Need</strong>,{' '}
          <strong>In Stock</strong> and <strong>Short</strong> are loose units;{' '}
          <strong>To Order</strong> is in <strong>strips</strong>, because that is the unit an order is
          placed in. You only buy the gap, rounded up to whole strips — items you already hold enough
          of are not ticked. Every quantity can be edited.
        </Typography>

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && !error && lines.length === 0 && (
          <Alert severity="info">Nothing left to order — every refill medicine has already been collected this cycle.</Alert>
        )}

        {!loading && needed.length === 0 && lines.length > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Nothing needs ordering — every medicine these customers are due is already in stock.
          </Alert>
        )}

        {!loading && covered.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              To order · covered ({covered.length})
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
              Short or out of stock, and bought by two or more customers — so the stock will move
              even if one person does not collect. Quantity defaults to the shortfall rounded up to whole strips, not the full demand.
            </Typography>
            {renderTable(covered, 'covered')}
          </>
        )}

        {!loading && sole.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Warning sx={{ fontSize: 18, color: '#ed6c02' }} />
              To order · sole customer, confirm first ({sole.length})
            </Typography>
            <Alert severity="warning" sx={{ mb: 1 }}>
              Only one customer buys each of these. If you buy them and that person does not collect,
              they become dead stock — tick only what the customer has confirmed.
            </Alert>
            {renderTable(sole, 'sole')}
          </>
        )}

        {!loading && inStock.length > 0 && (
          <>
            <Button size="small" onClick={() => setShowInStock(v => !v)} sx={{ mb: 1 }}>
              {showInStock ? 'Hide' : 'Show'} {inStock.length} medicine{inStock.length === 1 ? '' : 's'} already in stock
            </Button>
            {showInStock && (
              <>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                  These are due this cycle but you already hold enough — nothing to buy. Tick one only
                  if you want to top it up anyway.
                </Typography>
                {renderTable(inStock, 'instock')}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mr: 'auto' }}>
          {chosen.length} medicine{chosen.length === 1 ? '' : 's'} · {totalStrips} strip{totalStrips === 1 ? '' : 's'} ({totalLoose} loose)
          {chosen.length > 0 && <> · approx {inr(chosen.reduce((s, l) => s + Number(l.est_amount || 0), 0))}</>}
        </Typography>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate}
          disabled={saving || loading || chosen.length === 0 || !staffId}
          startIcon={saving ? <CircularProgress size={16} /> : <ShoppingCart />}>
          {saving ? 'Creating…' : isBulk ? 'Create Consolidated Order' : 'Create Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MonthlyRefillOrderReview;
